import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectCard from './components/ProjectCard';
import ProjectFilter from './components/ProjectFilter';
import ProjectModal from './components/ProjectModal';
import FeaturedProject from './components/FeaturedProject';
import GitHubStats from './components/GitHubStats';
import { getGitHubStats } from '../../services/githubService';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTech, setSelectedTech] = useState('All');
  const [modalProject, setModalProject] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [liveGitHubData, setLiveGitHubData] = useState(null);

  // Fetch live GitHub data on mount
  useEffect(() => {
    async function fetchGitHub() {
      try {
        const data = await getGitHubStats();
        if (data) {
          setLiveGitHubData(data);
        }
      } catch (err) {
        console.warn('Failed to fetch GitHub data for portfolio stats:', err);
      }
    }
    fetchGitHub();
  }, []);

  // Dynamically generate projects from GitHub repos
  const defaultImage = 'https://raw.githubusercontent.com/mustanaliev/portfolio-assets/main/default-preview.png';
  const staticProjects = [
    {
      id: 1,
      title: 'fullstackopen-part13',
      category: 'TypeScript',
      description: 'Advanced Full Stack Open course exercises (part 13)',
      fullDescription: 'Advanced Full Stack Open course exercises (part 13)',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?fit=crop&w=600&q=80',
      imageAlt: 'fullstackopen-part13 GitHub repository',
      technologies: ['TypeScript', 'React', 'Node.js'],
      status: 'Live',
      featured: false,
      stars: '0',
      forks: '0',
      lastUpdated: '',
      demoUrl: 'https://github.com/Mustan-dev/fullstackopen-part13',
      githubUrl: 'https://github.com/Mustan-dev/fullstackopen-part13',
      features: [],
      codePreview: '',
      recentCommits: []
    },
    {
      id: 2,
      title: 'real-estate-big-data-analysis',
      category: 'Jupyter Notebook',
      description: 'Big data analysis for real estate using Jupyter Notebook',
      fullDescription: 'Big data analysis for real estate using Jupyter Notebook',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?fit=crop&w=600&q=80',
      imageAlt: 'real-estate-big-data-analysis GitHub repository',
      technologies: ['Python', 'Jupyter', 'Pandas'],
      status: 'Live',
      featured: false,
      stars: '0',
      forks: '0',
      lastUpdated: '',
      demoUrl: 'https://github.com/Mustan-dev/real-estate-big-data-analysis',
      githubUrl: 'https://github.com/Mustan-dev/real-estate-big-data-analysis',
      features: [],
      codePreview: '',
      recentCommits: []
    },
    {
      id: 3,
      title: 'techxcel-infrastructure-simulation',
      category: 'Python',
      description: 'Python simulation for infrastructure projects',
      fullDescription: 'Python simulation for infrastructure projects',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?fit=crop&w=600&q=80',
      imageAlt: 'techxcel-infrastructure-simulation GitHub repository',
      technologies: ['Python', 'Simulation'],
      status: 'Live',
      featured: false,
      stars: '0',
      forks: '0',
      lastUpdated: '',
      demoUrl: 'https://github.com/Mustan-dev/techxcel-infrastructure-simulation',
      githubUrl: 'https://github.com/Mustan-dev/techxcel-infrastructure-simulation',
      features: [],
      codePreview: '',
      recentCommits: []
    },
    {
      id: 4,
      title: 'WindowsFormsApp',
      category: 'C#',
      description: 'C# Windows Forms application',
      fullDescription: 'C# Windows Forms application',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?fit=crop&w=600&q=80',
      imageAlt: 'WindowsFormsApp GitHub repository',
      technologies: ['C#', 'WinForms'],
      status: 'Live',
      featured: false,
      stars: '0',
      forks: '0',
      lastUpdated: '',
      demoUrl: 'https://github.com/Mustan-dev/WindowsFormsApp',
      githubUrl: 'https://github.com/Mustan-dev/WindowsFormsApp',
      features: [],
      codePreview: '',
      recentCommits: []
    },
    {
      id: 5,
      title: 'Java-Student-Record-System',
      category: 'Java',
      description: 'Student record system built in Java',
      fullDescription: 'Student record system built in Java',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?fit=crop&w=600&q=80',
      imageAlt: 'Java-Student-Record-System GitHub repository',
      technologies: ['Java', 'Swing'],
      status: 'Live',
      featured: false,
      stars: '0',
      forks: '0',
      lastUpdated: '',
      demoUrl: 'https://github.com/Mustan-dev/Java-Student-Record-System',
      githubUrl: 'https://github.com/Mustan-dev/Java-Student-Record-System',
      features: [],
      codePreview: '',
      recentCommits: []
    },
    {
      id: 6,
      title: 'Python-Student-Record-Sytem',
      category: 'Python',
      description: 'Student record system built in Python',
      fullDescription: 'Student record system built in Python',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?fit=crop&w=600&q=80',
      imageAlt: 'Python-Student-Record-Sytem GitHub repository',
      technologies: ['Python', 'Tkinter'],
      status: 'Live',
      featured: false,
      stars: '0',
      forks: '0',
      lastUpdated: '',
      demoUrl: 'https://github.com/Mustan-dev/Python-Student-Record-Sytem',
      githubUrl: 'https://github.com/Mustan-dev/Python-Student-Record-Sytem',
      features: [],
      codePreview: '',
      recentCommits: []
    }
  ];

  const projects = (liveGitHubData?.repositories?.nodes?.length > 0
    ? liveGitHubData.repositories.nodes.map((repo, idx) => ({
        id: idx + 1,
        title: repo.name,
        category: repo.primaryLanguage?.name || 'Other',
        description: repo.description || 'No description provided.',
        fullDescription: repo.description || 'No description provided.',
        image:
          repo.hasPreviewImage
            ? `https://raw.githubusercontent.com/${liveGitHubData.login}/${repo.name}/main/preview.png`
            : 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?fit=crop&w=600&q=80',
        imageAlt: `${repo.name} GitHub repository`,
        technologies: [repo.primaryLanguage?.name].filter(Boolean),
        status: 'Live',
        featured: false,
        stars: repo.stargazerCount?.toString() || '0',
        forks: repo.forkCount?.toString() || '0',
        users: '',
        watchers: '',
        commits: '',
        performance: '',
        lastUpdated: repo.pushedAt,
        demoUrl: repo.homepage || repo.url,
        githubUrl: repo.url,
        features: [],
        codePreview: '',
        recentCommits: []
      }))
    : staticProjects);

  // Mock GitHub stats
  const githubStats = {
    totalCommits: "2,847",
    publicRepos: "47",
    totalStars: "8.9k",
    totalForks: "1.2k",
    recentActivity: [
      { type: "commit", message: "Added new portfolio project showcase", date: "2 hours ago" },
      { type: "star", message: "Received star on E-Commerce Platform repository", date: "5 hours ago" },
      { type: "commit", message: "Updated AI content generator documentation", date: "1 day ago" },
      { type: "fork", message: "Weather Analytics Dashboard was forked", date: "2 days ago" }
    ]
  };

  // Get unique categories and technologies
  const categories = ['All', ...new Set(projects.map((p) => p.category))];
  const technologies = ['All', ...new Set(projects.flatMap((p) => p.technologies))];

  // Filter projects
  const filteredProjects = useMemo(() => {
    // If "All" is selected for both, show all projects
    if (selectedCategory === 'All' && selectedTech === 'All') {
      return projects;
    }
    return projects?.filter((project) => {
      const categoryMatch = selectedCategory === 'All' || project?.category === selectedCategory;
      const techMatch = selectedTech === 'All' || project?.technologies?.includes(selectedTech);
      return categoryMatch && techMatch;
    });
  }, [selectedCategory, selectedTech, projects]);

  // Get featured project
  const featuredProject = projects?.find((p) => p?.featured);

  const handleViewDemo = (project) => {
    setModalProject(project);
    setModalType('demo');
    setIsModalOpen(true);
  };

  const handleViewCode = (project) => {
    setModalProject(project);
    setModalType('code');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalProject(null);
    setModalType(null);
  };

  const handleClearFilters = () => {
    setSelectedCategory('All');
    setSelectedTech('All');
  };

  return (
    <div className="min-h-screen bg-background">
  <Helmet>
        <title>Portfolio - Mustan-Dev | Interactive Project Showcase</title>
        <meta name="description" content="Explore my comprehensive portfolio featuring interactive demos, case studies, and technical deep-dives into full-stack development projects." />
        <meta name="keywords" content="portfolio, projects, full-stack development, React, Node.js, web development, software engineer" />
  </Helmet>
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-16 bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Icon name="Briefcase" size={24} className="text-primary" />
                <span className="px-3 py-1 text-sm font-medium bg-primary/10 text-primary rounded-full">Project Showcase</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">My <span className="text-gradient-brand">Portfolio</span></h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">Explore my comprehensive collection of projects featuring interactive demos, detailed case studies, and technical deep-dives into modern web development solutions.</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-1">
                  {liveGitHubData?.repositories?.totalCount || projects?.length}
                </div>
                <div className="text-sm text-muted-foreground">Public Repos</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-1">{technologies?.length - 1}</div>
                <div className="text-sm text-muted-foreground">Technologies</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-1">
                  {liveGitHubData?.repositories?.nodes?.reduce((sum, repo) => sum + (repo.stargazerCount || 0), 0) || '0'}
                </div>
                <div className="text-sm text-muted-foreground">Total Stars</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-1">
                  {liveGitHubData?.repositories?.nodes?.reduce((sum, repo) => sum + (repo.forkCount || 0), 0) || '0'}
                </div>
                <div className="text-sm text-muted-foreground">Total Forks</div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* GitHub Stats */}
            <GitHubStats stats={githubStats} />

            {/* Featured Project */}
            {featuredProject && (
              <div className="mb-16">
                <FeaturedProject project={featuredProject} onViewDemo={handleViewDemo} onViewCode={handleViewCode} />
              </div>
            )}

            {/* Project Filter */}
            <ProjectFilter categories={categories} technologies={technologies} selectedCategory={selectedCategory} selectedTech={selectedTech} onCategoryChange={setSelectedCategory} onTechChange={setSelectedTech} onClearFilters={handleClearFilters} projectCount={filteredProjects?.length} />

            {/* Projects Grid */}
            <div id="projects" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects?.filter((project) => !project?.featured)?.map((project) => (
                <ProjectCard key={project?.id} project={project} onViewDemo={handleViewDemo} onViewCode={handleViewCode} />
              ))}
            </div>

            {/* Empty State */}
            {filteredProjects?.length === 0 && (
              <div className="text-center py-16">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No projects found</h3>
                <p className="text-muted-foreground mb-6">Try adjusting your filters to see more projects.</p>
                <Button variant="outline" onClick={handleClearFilters} iconName="RotateCcw" iconPosition="left">Clear Filters</Button>
              </div>
            )}

            {/* Call to Action */}
            <div className="mt-16 text-center bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 rounded-2xl p-8 md:p-12">
              <Icon name="MessageCircle" size={48} className="text-primary mx-auto mb-6" />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">Interested in Working Together?</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">I'm always excited to collaborate on innovative projects and bring creative ideas to life. Let's discuss how we can work together to build something amazing.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/contact">
                  <Button variant="default" size="lg" iconName="Mail" iconPosition="left" className="bg-gradient-to-r from-primary to-accent hover:shadow-lg">Get In Touch</Button>
                </a>
                <Button variant="outline" size="lg" iconName="Download" iconPosition="left">Download Resume</Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Project Modal */}
      <ProjectModal project={modalProject} isOpen={isModalOpen} onClose={handleCloseModal} type={modalType} />

    </div>
  );
};

export default Portfolio;
